import './Tabs.scss';
import React, {
    createRef,
    ReactNode,
    RefObject,
    startTransition,
    useCallback,
    useContext,
    useEffect,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { flushSync } from 'react-dom';
import { Tabs as MantineTabs } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import { clsx } from 'clsx';
import { MdMenu } from 'react-icons/md';
import type { ITab } from '../../types/ITab';
import Button from '../../atoms/Button';
import { MenuContext } from '../../atoms/Menu/MenuContext';
import { TabsContext } from '../../context/TabsContext';
import { TabPanelHiddenContext } from '../../context/TabPanelHiddenContext';
import { useMenuKeyboard } from '../../hooks/useMenuKeyboard';
import { buildUrlWithReturnTo } from '@srm/core/routing';
import { useUi } from '../../provider/UiContext';
import AnchoredDialog from '../AnchoredDialog/AnchoredDialog';

type TabRef = HTMLButtonElement;

const TabSlot = React.memo(
    ({
        isActive,
        isLoaded,
        keepMounted,
        content,
        panelId,
        tabId,
    }: {
        isActive: boolean;
        isLoaded: boolean;
        keepMounted: boolean;
        content: ReactNode;
        panelId: string;
        tabId: string;
    }) => {
        // скрыта и панель внутри скрытой панели внешних вкладок
        const parentHidden = useContext(TabPanelHiddenContext) === true;
        const hidden = !isActive || parentHidden;
        return (
            <div
                id={panelId}
                role="tabpanel"
                aria-labelledby={tabId}
                aria-hidden={!isActive || undefined}
                className={clsx('rf-tabs__content_box', !isActive && 'rf-tabs__content_hide')}
            >
                <TabPanelHiddenContext value={hidden}>
                    {isActive || isLoaded || keepMounted ? content : null}
                </TabPanelHiddenContext>
            </div>
        );
    },
);

const getTabValue = (tab: ITab, index: number) => tab.value ?? tab.url ?? String(index);
const getSafeIdPart = (value: string) => value.replace(/[^a-zA-Z0-9_-]/g, '_');
const getTabIndexByValue = (list: ITab[], value: string | null) =>
    value === null ? -1 : list.findIndex((tab, index) => getTabValue(tab, index) === value);

const isHashOrPathMatch = (tab: ITab, hash: string, pathname: string): boolean => {
    if (!tab.url) {
        return false;
    }

    if (tab.url.startsWith('#')) {
        return tab.exact ? tab.url === hash : hash.includes(tab.url);
    }

    return tab.exact ? tab.url === pathname : pathname.includes(tab.url);
};

const getFallbackActiveIndex = (list: ITab[]) => {
    const activeIndex = list.findIndex((tab) => tab.active && !tab.disabled);
    if (activeIndex !== -1) {
        return activeIndex;
    }

    return list.findIndex((tab) => !tab.disabled);
};

const findActiveIndexByUrl = (list: ITab[], isRouting: boolean): number => {
    const hash = window.location.hash;
    const pathname = window.location.pathname;

    if (isRouting) {
        const byUrl = list.findIndex((tab) => isHashOrPathMatch(tab, hash, pathname));
        if (byUrl !== -1) {
            return byUrl;
        }
    }

    return getFallbackActiveIndex(list);
};

const getInitialActiveValue = (list: ITab[]) => {
    const isRouting = list.length > 0 && list.every((tab) => tab.url);
    const activeIndex =
        typeof window === 'undefined' ? getFallbackActiveIndex(list) : findActiveIndexByUrl(list, isRouting);

    return activeIndex === -1 ? null : getTabValue(list[activeIndex], activeIndex);
};

export interface ITabsProps {
    /** Список вкладок */
    list: ITab[];
    /**
     * @default true
     * Показывать линию под табами
     * */
    showLine?: boolean;
    /**
     * Показывать меню если табы не вмещаются в контейре
     * @default true
     */
    showMenu?: boolean;
    /**
     * Размер табов
     * @default 'md'
     */
    size?: 'md' | 'sm';
    /**
     * Создавать панели неактивных вкладок. Без keepMounted контент монтируется
     * при первой активации и затем сохраняется в скрытой панели.
     * @default true
     */
    renderInactiveTabs?: boolean;

    /** Если во вкладках есть url, то через children пробрасывается <Router/> */
    children?: ReactNode | ReactNode[];

    onIndexChange?: (tabIndex: number) => void;
    /** Класс */
    className?: string;
    /**
     * Программное переключение: при изменении на непустой value активирует
     * соответствующую вкладку (как клик). null/undefined ничего не делает —
     * это команда, а не controlled-значение.
     */
    activeValue?: string | null;
    /**
     * `activate` повторяет пользовательский клик вместе с handler; `sync` только
     * синхронизирует индикатор с внешним источником истины (например, URL).
     * @default 'activate'
     */
    activeValueBehavior?: 'activate' | 'sync';
    /** Позволяет повторно выполнить команду activeValue с тем же значением. */
    activeValueNonce?: number;
    /** Вызывается после того, как программно выбранная панель стала активной. */
    onActiveValueReady?: (value: string, nonce: number | undefined, panel: HTMLElement | null) => void;
    /** Подпись навигации для экранного диктора. */
    navigationAriaLabel?: string;
    /** Рендерить встроенные tabpanel. Выключается, если панель живёт снаружи Tabs. */
    renderContent?: boolean;
    /** Идентификаторы внешней вкладки и панели для navigation-only режима. */
    tabIdResolver?: (value: string) => string;
    panelIdResolver?: (value: string) => string;
    /**
     * Монтировать содержимое всех вкладок сразу (неактивные — скрытыми), как `keepMounted` у каждой вкладки.
     * @default false
     */
    preLoadedTabs?: boolean;
    /**
     * Спрашивается перед переключением вкладки пользователем (щелчок, клавиатура, меню скрытых вкладок):
     * `false` или отказ промиса оставляют текущую вкладку. Повторный выбор активной вкладки и программное
     * переключение (`activeValue`, адрес) не спрашиваются. Индексы — в `list`.
     */
    onBeforeChange?: (nextIndex: number, currentIndex: number) => Promise<boolean | void> | boolean | void;
    /** Дополнительные data-атрибуты кнопки вкладки по её значению (якоря диагностики приложения). */
    tabDataAttributes?: (value: string) => Record<`data-${string}`, string | undefined>;
}

const isPromiseLike = (value: unknown): value is PromiseLike<unknown> =>
    typeof (value as PromiseLike<unknown> | null | undefined)?.then === 'function';

/**
 * Кромка элемента вне дорожки полосы (кнопка меню «ещё») в координатах дорожки — по offset-цепочке, как у вкладок:
 * масштаб предков (окно открывается с transform) раскладку не меняет, а прямоугольники его учли бы.
 */
const getBoxInTrack = (element: HTMLElement, track: HTMLElement) => {
    const origin = track.offsetParent;
    let left = 0;
    let node: Element | null = element;
    while (node instanceof HTMLElement && node !== origin) {
        left += node.offsetLeft;
        node = node.offsetParent;
        if (node instanceof HTMLElement && node !== origin) left += node.clientLeft;
    }

    if (!origin || node !== origin) {
        // раскладки нет (jsdom) — по прямоугольникам
        const rect = element.getBoundingClientRect();
        return { left: rect.left - track.getBoundingClientRect().left - track.clientLeft, width: rect.width };
    }

    return { left: left - track.offsetLeft - track.clientLeft, width: element.offsetWidth };
};

interface IActiveValueCommand {
    value: string;
    nonce: number | undefined;
    /** Команда `activate` выбрала вкладку: её обработчики (onIndexChange, handler, адрес) зовёт эффект после коммита. */
    activate: boolean;
    /**
     * Как показывается панель выбранной командой вкладки: `now` — в том же рендере (панель уже смонтирована),
     * `frame` — после кадра переходом, как у клика (иначе монтаж тяжёлой панели шёл бы в срочной перерисовке
     * родителя одним куском); `none` — команда вкладку не выбрала.
     */
    commit: 'none' | 'now' | 'frame';
}

/** Вкладка, которую может выбрать команда activeValue: есть в списке и доступна. */
const isSelectableValue = (list: ITab[], value: string) => {
    const index = getTabIndexByValue(list, value);
    return index !== -1 && !list[index]?.disabled;
};

/**
 * Команда activeValue при выбранной `selected`: что запомнить и выбирает ли она вкладку (`applies`). `mounted` —
 * панель вкладки уже смонтирована: тогда она показывается сразу, иначе — после кадра.
 */
const commandOf = (
    list: ITab[],
    activeValue: string | null | undefined,
    activeValueNonce: number | undefined,
    activeValueBehavior: ITabsProps['activeValueBehavior'],
    selected: string | null,
    mounted: (value: string) => boolean,
): { command: IActiveValueCommand | undefined; applies: boolean } => {
    if (activeValue == null) return { command: undefined, applies: false };
    const applies = activeValue !== selected && isSelectableValue(list, activeValue);
    return {
        command: {
            value: activeValue,
            nonce: activeValueNonce,
            activate: applies && activeValueBehavior === 'activate',
            commit: !applies ? 'none' : mounted(activeValue) ? 'now' : 'frame',
        },
        applies,
    };
};

/** Выбор и команда на монтировании: команда activeValue сразу в начальном состоянии, без второго прохода рендера. */
const getInitialTabsState = (
    list: ITab[],
    activeValue: string | null | undefined,
    activeValueNonce: number | undefined,
    activeValueBehavior: ITabsProps['activeValueBehavior'],
) => {
    const defaultValue = getInitialActiveValue(list);
    // на монтировании панель выбранной вкладки и так монтируется первой
    const { command, applies } = commandOf(
        list,
        activeValue,
        activeValueNonce,
        activeValueBehavior,
        defaultValue,
        () => true,
    );
    return { selected: applies ? activeValue! : defaultValue, command };
};

interface ITabsOverflowMenuProps {
    items: { value: string; tab: ITab }[];
    selectedValue: string | null;
    onSelect: (value: string) => void;
}

/** Меню скрытых по ширине вкладок: вёрстка пунктов своя, клавиатура и закрытие — как у меню пакета. */
const TabsOverflowMenu = ({ items, selectedValue, onSelect }: ITabsOverflowMenuProps) => {
    const { show, onClose, closeToTrigger, menuId } = useContext(MenuContext);
    const menuRef = useRef<HTMLDivElement>(null);
    const onKeyDown = useMenuKeyboard(menuRef, show);
    const close = closeToTrigger ?? onClose;

    return (
        <div ref={menuRef} id={menuId} role="menu" aria-label="Скрытые вкладки" tabIndex={-1} onKeyDown={onKeyDown}>
            {items.map(({ value, tab }) => (
                <div key={value} className={clsx('rf-tabs__link', 'rf-tabs__menu-link')}>
                    <button
                        type="button"
                        role="menuitemradio"
                        aria-checked={value === selectedValue}
                        className={clsx(
                            'rf-tabs__button',
                            'rf-tabs__menu-button',
                            value === selectedValue && 'rf-tabs__menu-button--active',
                            tab.invalid && 'rf-tabs__button--invalid',
                        )}
                        disabled={tab.disabled}
                        onClick={() => {
                            onSelect(value);
                            close();
                        }}
                        title={tab.title ?? (tab.invalid ? 'В этом разделе есть ошибки валидации' : undefined)}
                    >
                        {!!tab.icon && <div className={clsx('rf-tabs__icon', 'rf-tabs__menu-icon')}>{tab.icon}</div>}
                        {!!tab.label && <div>{tab.label}</div>}
                    </button>
                </div>
            ))}
        </div>
    );
};

const Tabs: React.FC<ITabsProps> = ({
    list,
    showLine = true,
    showMenu = true,
    size = 'md',
    renderInactiveTabs = true,
    children,
    onIndexChange,
    className,
    activeValue,
    activeValueBehavior = 'activate',
    activeValueNonce,
    onActiveValueReady,
    navigationAriaLabel = 'Вкладки',
    renderContent = true,
    tabIdResolver,
    panelIdResolver,
    preLoadedTabs = false,
    onBeforeChange,
    tabDataAttributes,
}: ITabsProps) => {
    const { navigate } = useUi();
    const generatedId = useId();
    const refs = useRef<RefObject<TabRef | null>[]>([]);
    const listRef = useRef<ITab[]>(list);
    const [initialState] = useState(() =>
        getInitialTabsState(list, activeValue, activeValueNonce, activeValueBehavior),
    );
    // Выбор и показанная вкладка последнего закоммиченного рендера (layout-эффекты ниже) и запросы из обработчиков.
    // Рендер их не пишет: рендер команды activeValue прерванный переход может отбросить.
    // Состояния выбора — в обёртке { value }: setState в отброшенном рендере оставляет React своё значение как
    // последнее отрисованное, и тот же выбор из обработчика без новой обёртки React отбросил бы как «без изменений».
    const selectedValueRef = useRef<string | null>(initialState.selected);
    const committedValueRef = useRef<string | null>(initialState.selected);
    const activationFrame = useRef<number | null>(null);
    const navigationListRef = useRef<HTMLDivElement>(null);
    const inkRef = useRef<HTMLSpanElement>(null);
    const overflowMenuRef = useRef<HTMLDivElement>(null);
    const prevInkLeftRef = useRef<number | null>(null);
    const prevInkRightRef = useRef<number | null>(null);
    const inkAnimationRef = useRef<Animation | null>(null);
    const prevSelectedForInkRef = useRef<string | null>(selectedValueRef.current);
    const onActiveValueReadyRef = useRef(onActiveValueReady);
    onActiveValueReadyRef.current = onActiveValueReady;
    const [lastVisibleIndex, setLastVisibleIndex] = useState<number>(list.length - 1);
    const [isOverflowDialogOpen, setIsOverflowDialogOpen] = useState(false);
    const [{ value: selectedValue }, setSelectedValue] = useState<{ value: string | null }>({
        value: selectedValueRef.current,
    });
    useLayoutEffect(() => {
        selectedValueRef.current = selectedValue;
    }, [selectedValue]);
    const [{ value: committedValue }, setCommittedValue] = useState<{ value: string | null }>({
        value: committedValueRef.current,
    });
    useLayoutEffect(() => {
        committedValueRef.current = committedValue;
    }, [committedValue]);
    const [loadedTabValues, setLoadedTabValues] = useState<Set<string>>(() => {
        const initialValue = committedValueRef.current;
        return initialValue === null ? new Set() : new Set([initialValue]);
    });
    // Команда activeValue (значение + nonce) применяется в рендере, а не эффектом: смена вкладки извне (другой
    // элемент в карточке) видна в том же кадре, без кадра прежней вкладки и без rAF-задержки уже смонтированной панели.
    const [lastCommand, setLastCommand] = useState<IActiveValueCommand | undefined>(initialState.command);
    const handledCommandRef = useRef<IActiveValueCommand | undefined>(undefined);

    const renderPanels = renderInactiveTabs || preLoadedTabs;
    const isRouting = useMemo(() => list.length > 0 && list.every((tab) => tab.url), [list]);
    const selectedIndex = getTabIndexByValue(list, selectedValue);
    const committedIndex = getTabIndexByValue(list, committedValue);
    const isActiveIndexInMenu = selectedIndex > lastVisibleIndex;
    const tabsContextValue = useMemo(
        () => ({ activeTab: committedIndex === -1 ? 0 : committedIndex }),
        [committedIndex],
    );
    const getTabId = useCallback(
        (value: string) => tabIdResolver?.(value) ?? `${generatedId}-tab-${getSafeIdPart(value)}`,
        [generatedId, tabIdResolver],
    );
    const getPanelId = useCallback(
        (value: string) => panelIdResolver?.(value) ?? `${generatedId}-panel-${getSafeIdPart(value)}`,
        [generatedId, panelIdResolver],
    );

    const getWidthTab = (element: HTMLElement | null) => element?.getBoundingClientRect().width || 0;

    const handleLastVisibleIndex = useCallback(
        (containerWidth = 0) => {
            if (!showMenu) {
                return;
            }

            const MENU_WIDTH = 48;
            const hasHiddenIndex = lastVisibleIndex < list.length - 1;
            let visibleIndex = lastVisibleIndex;
            let visibleWidth = hasHiddenIndex ? MENU_WIDTH : 0;

            for (let index = 0; index < list.length; index++) {
                if (refs.current?.[index]?.current) {
                    const width = getWidthTab(refs.current[index].current);
                    if (containerWidth > visibleWidth + width) {
                        visibleWidth += width;
                    } else {
                        break;
                    }
                }
                visibleIndex = index;
            }

            if (hasHiddenIndex) {
                const nextIndex = visibleIndex + 1;
                const isLastIndex = nextIndex === refs.current.length - 1;
                const nextWidth = getWidthTab(refs.current?.[nextIndex]?.current);

                if (visibleWidth + nextWidth - (isLastIndex ? MENU_WIDTH : 0) <= containerWidth) {
                    visibleIndex = nextIndex;
                }
            }

            setLastVisibleIndex(visibleIndex);
        },
        [lastVisibleIndex, list.length, showMenu],
    );

    // Пересчёт видимых вкладок — по ширине после паузы ресайза, а не на каждом кадре: смена ширины перерисовывает
    // вкладки вместе с панелями
    const [width, setWidth] = useState<number>();
    const scheduleWidth = useDebouncedCallback(setWidth, 300);
    const ref = useCallback(
        (node: HTMLDivElement) => {
            const observer = new ResizeObserver(([entry]) => {
                if (entry) scheduleWidth(entry.contentRect.width);
            });
            observer.observe(node);
            return () => {
                observer.disconnect();
                scheduleWidth.cancel();
            };
        },
        [scheduleWidth],
    );

    const setSelectedValueState = useCallback((value: string | null) => {
        if (selectedValueRef.current === value) return;
        selectedValueRef.current = value;
        setSelectedValue({ value });
    }, []);

    const markTabLoaded = useCallback((value: string) => {
        setLoadedTabValues((prev) => {
            if (prev.has(value)) {
                return prev;
            }

            const next = new Set(prev);
            next.add(value);
            return next;
        });
    }, []);

    const setCommittedValueState = useCallback(
        (value: string | null) => {
            if (committedValueRef.current === value) return;
            committedValueRef.current = value;
            setCommittedValue({ value });
            if (value !== null) {
                markTabLoaded(value);
            }
        },
        [markTabLoaded],
    );

    if (
        activeValue == null
            ? lastCommand !== undefined
            : lastCommand?.value !== activeValue || lastCommand.nonce !== activeValueNonce
    ) {
        const next = commandOf(
            list,
            activeValue,
            activeValueNonce,
            activeValueBehavior,
            selectedValue,
            (value) =>
                renderPanels &&
                (preLoadedTabs || loadedTabValues.has(value) || !!list[getTabIndexByValue(list, value)]?.keepMounted),
        );
        setLastCommand(next.command);
        if (next.applies) {
            setSelectedValue({ value: activeValue! });
            // незагруженную панель покажет эффект команды после кадра
            if (next.command?.commit === 'now') setCommittedValue({ value: activeValue! });
        }
    }

    const scheduleCommittedValue = useCallback(
        (value: string) => {
            if (activationFrame.current !== null) {
                window.cancelAnimationFrame(activationFrame.current);
            }

            // Give the selected tab indicator one paint before mounted panels and grids react to activeTab.
            activationFrame.current = window.requestAnimationFrame(() => {
                activationFrame.current = null;
                startTransition(() => {
                    // команда activeValue могла выбрать другую вкладку раньше кадра
                    if (selectedValueRef.current === value && getTabIndexByValue(listRef.current, value) !== -1) {
                        setCommittedValueState(value);
                    }
                });
            });
        },
        [setCommittedValueState],
    );

    const cancelScheduledCommit = useCallback(() => {
        if (activationFrame.current !== null) {
            window.cancelAnimationFrame(activationFrame.current);
            activationFrame.current = null;
        }
    }, []);

    const runTabCallbacks = useCallback(
        (value: string) => {
            const index = getTabIndexByValue(listRef.current, value);
            const tab = index === -1 ? undefined : listRef.current[index];

            if (!tab) {
                return;
            }

            onIndexChange?.(index);
            tab.handler?.(refs.current[index]?.current ?? undefined);
        },
        [onIndexChange],
    );

    const navigateToTabUrl = useCallback(
        (url: string) => {
            if (url.startsWith('#')) {
                const histState = window.history.state;
                window.history.pushState(histState, '', window.location.pathname + window.location.search + url);
            } else {
                const currentReturnTo = new URLSearchParams(window.location.search).get('returnTo');
                void navigate(buildUrlWithReturnTo(url, currentReturnTo));
            }
        },
        [navigate],
    );

    const activateTabValue = useCallback(
        (value: string) => {
            const index = getTabIndexByValue(listRef.current, value);
            const tab = index === -1 ? undefined : listRef.current[index];
            if (!tab || tab.disabled) {
                return;
            }

            if (isRouting && tab.url && selectedValueRef.current !== value) {
                navigateToTabUrl(tab.url);
            }

            if (selectedValueRef.current !== value) {
                flushSync(() => {
                    setSelectedValueState(value);
                });
            }

            runTabCallbacks(value);

            if (committedValueRef.current === value) {
                cancelScheduledCommit();
            } else {
                scheduleCommittedValue(value);
            }
        },
        [
            cancelScheduledCommit,
            isRouting,
            navigateToTabUrl,
            runTabCallbacks,
            scheduleCommittedValue,
            setSelectedValueState,
        ],
    );

    const onBeforeChangeRef = useRef(onBeforeChange);
    onBeforeChangeRef.current = onBeforeChange;
    const changeRequestRef = useRef(0);

    /** Выбор вкладки пользователем: без `onBeforeChange` — сразу (индикатор синхронно), иначе после его согласия. */
    const requestTabValue = useCallback(
        (value: string) => {
            const guard = onBeforeChangeRef.current;
            const request = ++changeRequestRef.current;
            const nextIndex = getTabIndexByValue(listRef.current, value);
            const currentIndex = getTabIndexByValue(listRef.current, selectedValueRef.current);
            if (!guard || nextIndex === -1 || nextIndex === currentIndex || listRef.current[nextIndex]?.disabled) {
                activateTabValue(value);
                return;
            }

            let allowed = undefined as ReturnType<typeof guard>;
            try {
                // Колбэк обычно запоминает вкладку в состоянии родителя — переходом: иначе flushSync индикатора
                // забирает и эту перерисовку (вся карточка со смонтированными вкладками), и полоса трогается с места,
                // когда отрисовка уже съела её анимацию.
                startTransition(() => {
                    allowed = guard(nextIndex, currentIndex);
                });
            } catch {
                return;
            }

            if (!isPromiseLike(allowed)) {
                if (allowed !== false) activateTabValue(value);
                return;
            }

            // Пока ответ шёл, могли выбрать другую вкладку — применяется только последний выбор.
            allowed.then(
                (result) => {
                    if (result !== false && request === changeRequestRef.current) activateTabValue(value);
                },
                () => undefined,
            );
        },
        [activateTabValue],
    );

    const handleMantineChange = useCallback(
        (value: string | null) => {
            if (value === null) {
                return;
            }

            requestTabValue(value);
        },
        [requestTabValue],
    );

    const positionInk = useCallback(
        (animate: boolean) => {
            const ink = inkRef.current;
            if (!ink) {
                return;
            }

            const index = getTabIndexByValue(list, selectedValue);
            const tab = index === -1 ? undefined : list[index];
            const track = navigationListRef.current;
            // Активная вкладка уехала в меню «ещё» (скрыта по ширине) — полоса стоит под кнопкой меню.
            const inMenu = index > lastVisibleIndex;
            const target =
                index === -1
                    ? null
                    : inMenu
                      ? overflowMenuRef.current?.querySelector<HTMLElement>('.rf-tabs__menu__btn')
                      : refs.current[index]?.current;

            // Полоса не нужна, если активного таба нет или он выключен.
            if (!target || !track || !tab || tab.disabled) {
                ink.style.opacity = '0';
                prevInkLeftRef.current = null;
                prevInkRightRef.current = null;
                return;
            }

            const { left, width } = inMenu
                ? getBoxInTrack(target, track)
                : { left: target.offsetLeft, width: target.offsetWidth };
            const right = Math.max(0, track.clientWidth - (left + width));
            const prevLeft = prevInkLeftRef.current;
            const prevRight = prevInkRightRef.current;

            // Controlled-вкладки часто сразу перерисовывают родителя (например,
            // синхронизируют URL). Если геометрия уже выставлена, повторный layout
            // не должен обрывать бегунок.
            if (!animate && prevLeft === left && prevRight === right) {
                ink.style.opacity = '1';
                return;
            }

            const reduceMotion =
                typeof window.matchMedia === 'function' &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            const canAnimate = animate && !reduceMotion && prevLeft !== null && typeof ink.animate === 'function';

            // Видимая сейчас полоса (с учётом идущей анимации) — от неё и поедет новая: частые клики не дёргают её.
            let from: { x0: number; x1: number } | null = null;
            if (canAnimate) {
                const inkRect = ink.getBoundingClientRect();
                const trackRect = track.getBoundingClientRect();
                from = { x0: inkRect.left - trackRect.left, x1: inkRect.right - trackRect.left };
            }
            inkAnimationRef.current?.cancel();
            inkAnimationRef.current = null;

            // Полоса сразу встаёт на место (left/right), а путь до него рисует transform — его ведёт компоновщик,
            // и он не прерывается, пока главный поток занят отрисовкой содержимого вкладки (таблицы и т. п.).
            ink.style.transition = 'none';
            ink.style.left = `${left}px`;
            ink.style.right = `${right}px`;
            ink.style.opacity = '1';

            if (from && width > 0 && from.x1 > from.x0) {
                const to = { x0: left, x1: left + width };
                const goingRight = to.x0 >= from.x0;
                // Хвостовая кромка отстаёт → полоса упруго растягивается по ходу движения.
                const lead = 0.85;
                const tail = 0.35;
                const mid = {
                    x0: from.x0 + (to.x0 - from.x0) * (goingRight ? tail : lead),
                    x1: from.x1 + (to.x1 - from.x1) * (goingRight ? lead : tail),
                };
                const frame = (box: { x0: number; x1: number }) => ({
                    transform: `translateX(${box.x0 - left}px) scaleX(${(box.x1 - box.x0) / width})`,
                });
                inkAnimationRef.current = ink.animate([frame(from), { ...frame(mid), offset: 0.45 }, frame(to)], {
                    duration: 420,
                    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
                });
            } else {
                // зафиксировать позицию без анимации перед следующим кадром
                void ink.offsetWidth;
            }

            prevInkLeftRef.current = left;
            prevInkRightRef.current = right;
        },
        [list, selectedValue, lastVisibleIndex],
    );

    useLayoutEffect(() => {
        const animate = prevSelectedForInkRef.current !== selectedValue;
        prevSelectedForInkRef.current = selectedValue;
        positionInk(animate);
    }, [positionInk, selectedValue, lastVisibleIndex, width]);

    useLayoutEffect(() => {
        const navigationList = navigationListRef.current;
        if (!navigationList) return;

        const observer = new ResizeObserver(() => positionInk(false));
        observer.observe(navigationList);
        return () => observer.disconnect();
    }, [positionInk]);

    useEffect(() => {
        listRef.current = list;
    }, [list]);

    useEffect(() => {
        if (!lastCommand || lastCommand.commit === 'none' || handledCommandRef.current === lastCommand) return;
        handledCommandRef.current = lastCommand;
        if (lastCommand.commit === 'frame') {
            scheduleCommittedValue(lastCommand.value);
        } else {
            // выбор уже применён в рендере: отложенный коммит прежнего клика его не перебьёт
            cancelScheduledCommit();
        }
        if (!lastCommand.activate) return;
        const index = getTabIndexByValue(listRef.current, lastCommand.value);
        const url = listRef.current[index]?.url;
        if (isRouting && url) navigateToTabUrl(url);
        runTabCallbacks(lastCommand.value);
    }, [cancelScheduledCommit, isRouting, lastCommand, navigateToTabUrl, runTabCallbacks, scheduleCommittedValue]);

    useEffect(() => {
        if (activeValue != null && committedValue === activeValue) {
            onActiveValueReadyRef.current?.(
                activeValue,
                activeValueNonce,
                document.getElementById(getPanelId(activeValue)),
            );
        }
    }, [activeValue, activeValueNonce, committedValue, getPanelId]);

    useEffect(() => {
        refs.current = refs.current.slice(0, list.length);
    }, [list.length]);

    useEffect(
        () => () => {
            if (activationFrame.current !== null) {
                window.cancelAnimationFrame(activationFrame.current);
            }
            // ответ onBeforeChange, пришедший после ухода со страницы, не должен менять её адрес
            changeRequestRef.current += 1;
        },
        [],
    );

    useEffect(() => {
        if (!showMenu) {
            setLastVisibleIndex(list.length - 1);
        }
    }, [showMenu, list.length]);

    useEffect(() => {
        const syncActiveFromUrl = () => {
            const urlIndex = isRouting ? findActiveIndexByUrl(list, isRouting) : -1;
            let indexTab = urlIndex >= 0 && !list[urlIndex]?.disabled ? urlIndex : -1;

            if (indexTab === -1) {
                const selectedValueIndex = getTabIndexByValue(list, selectedValueRef.current);

                if (selectedValueIndex !== -1 && !list[selectedValueIndex]?.disabled) {
                    if (getTabIndexByValue(list, committedValueRef.current) === -1) {
                        setCommittedValueState(getTabValue(list[selectedValueIndex], selectedValueIndex));
                    }
                    return;
                }

                const committedValueIndex = getTabIndexByValue(list, committedValueRef.current);
                if (committedValueIndex !== -1 && !list[committedValueIndex]?.disabled) {
                    const nextValue = getTabValue(list[committedValueIndex], committedValueIndex);
                    setSelectedValueState(nextValue);
                    setCommittedValueState(nextValue);
                    return;
                }
            }

            if (indexTab === -1) {
                indexTab = getFallbackActiveIndex(list);
            }

            if (indexTab === -1) {
                return;
            }

            const nextValue = getTabValue(list[indexTab], indexTab);
            setSelectedValueState(nextValue);
            setCommittedValueState(nextValue);

            const newUrl = list[indexTab].url;
            if (isRouting && newUrl?.startsWith('#') && window.location.hash !== newUrl) {
                const histState = window.history.state;
                window.history.replaceState(histState, '', window.location.pathname + window.location.search + newUrl);
            }
        };

        syncActiveFromUrl();

        window.addEventListener('popstate', syncActiveFromUrl);
        return () => window.removeEventListener('popstate', syncActiveFromUrl);
    }, [isRouting, list, setCommittedValueState, setSelectedValueState]);

    const nav = list.map((tab, index) => {
        if (!refs.current[index]) {
            refs.current[index] = createRef<TabRef>();
        }

        const tabValue = getTabValue(tab, index);
        const isHidden = index > lastVisibleIndex;

        return (
            <MantineTabs.Tab
                key={tabValue}
                value={tabValue}
                className={clsx(
                    'rf-tabs__link',
                    isHidden && 'rf-tabs__link--hidden',
                    'rf-tabs__button',
                    tabValue === selectedValue && 'rf-tabs__button--active',
                    tab.invalid && 'rf-tabs__button--invalid',
                    `rf-tabs__button--${size}`,
                )}
                classNames={{ tabLabel: 'rf-tabs__button-label' }}
                ref={refs.current[index]}
                aria-hidden={isHidden || undefined}
                disabled={tab.disabled}
                tabIndex={isHidden ? -1 : undefined}
                id={getTabId(tabValue)}
                aria-controls={getPanelId(tabValue)}
                {...tabDataAttributes?.(tabValue)}
                title={tab.title ?? (tab.invalid ? 'В этом разделе есть ошибки валидации' : undefined)}
            >
                {!!tab.icon && <div className="rf-tabs__icon">{tab.icon}</div>}
                {!!tab.label && <div>{tab.label}</div>}
            </MantineTabs.Tab>
        );
    });

    const tabsContent = renderPanels ? (
        list.map((tab, index) => {
            const tabValue = getTabValue(tab, index);

            // Ключи вкладок — только значение: с индексом в ключе исчезновение вкладки перед
            // активной (правило видимости по режиму) перемонтировало её содержимое — пустой
            // кадр и потерянная прокрутка.
            return (
                <TabSlot
                    key={tabValue}
                    isActive={tabValue === committedValue}
                    isLoaded={loadedTabValues.has(tabValue)}
                    keepMounted={preLoadedTabs || !!tab.keepMounted}
                    content={tab.tab}
                    panelId={getPanelId(tabValue)}
                    tabId={getTabId(tabValue)}
                />
            );
        })
    ) : (
        <div
            id={committedIndex === -1 ? undefined : getPanelId(getTabValue(list[committedIndex], committedIndex))}
            role="tabpanel"
            aria-labelledby={
                committedIndex === -1 ? undefined : getTabId(getTabValue(list[committedIndex], committedIndex))
            }
            className="rf-tabs__content_box"
        >
            {committedIndex === -1 ? undefined : list[committedIndex]?.tab}
        </div>
    );

    const menuItems = list
        .slice(lastVisibleIndex + 1)
        .map((tab, hiddenIndex) => ({ value: getTabValue(tab, lastVisibleIndex + hiddenIndex + 1), tab }));

    const hasMenuNav = lastVisibleIndex < list.length - 1;

    useEffect(() => {
        if (!hasMenuNav) {
            setIsOverflowDialogOpen(false);
        }
    }, [hasMenuNav]);

    useLayoutEffect(() => {
        if (showMenu) {
            handleLastVisibleIndex(width ?? 0);
        } else if (lastVisibleIndex !== list.length - 1) {
            setLastVisibleIndex(list.length - 1);
        }
    }, [handleLastVisibleIndex, lastVisibleIndex, list.length, showMenu, width]);

    return (
        <TabsContext value={tabsContextValue}>
            <div className={clsx('rf-tabs', 'rf-tabs--buttons', className)} ref={ref}>
                <MantineTabs value={selectedValue} onChange={handleMantineChange} unstyled className="rf-tabs__mantine">
                    <nav className={clsx('rf-tabs__navigation', showLine && 'rf-tabs__navigation__line')}>
                        <MantineTabs.List
                            ref={navigationListRef}
                            className={clsx(
                                'rf-tabs__navigation-list',
                                !showMenu && 'rf-tabs__navigation-list--without-menu',
                            )}
                            aria-label={navigationAriaLabel}
                        >
                            {nav}
                            <span className="rf-tabs__ink" ref={inkRef} aria-hidden="true" data-testid="tabs-ink" />
                        </MantineTabs.List>
                        {hasMenuNav && (
                            <div
                                ref={overflowMenuRef}
                                className={clsx('rf-tabs__menu', isActiveIndexInMenu && 'rf-tabs__menu--active')}
                            >
                                <AnchoredDialog
                                    align="right"
                                    contentClassName="rf-tabs__menu-content"
                                    opened={isOverflowDialogOpen}
                                    onClose={() => setIsOverflowDialogOpen(false)}
                                    popupRole="menu"
                                    width={240}
                                    target={
                                        <Button
                                            variant="ghost"
                                            size="xs"
                                            aria-label="Показать скрытые вкладки"
                                            aria-expanded={isOverflowDialogOpen}
                                            aria-haspopup="menu"
                                            data-testid="button-menu-group__more"
                                            className="rf-tabs__menu__btn"
                                            startAdornment={<MdMenu />}
                                            title="Скрытые вкладки"
                                            onClick={() => setIsOverflowDialogOpen((isOpen) => !isOpen)}
                                        />
                                    }
                                >
                                    <TabsOverflowMenu
                                        items={menuItems}
                                        selectedValue={selectedValue}
                                        onSelect={requestTabValue}
                                    />
                                </AnchoredDialog>
                            </div>
                        )}
                    </nav>
                </MantineTabs>

                {renderContent && (children || list.length > 0) && (
                    <div className="rf-tabs__content">{children ? children : tabsContent}</div>
                )}
            </div>
        </TabsContext>
    );
};

export default Tabs;
